import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeApi } from "../../../finance-api-client";
import type { CreateBankAccount, UpdateBankAccount } from "../../../types";
import { queryKeys } from "../../query-keys";

// --- Bank Accounts ---

export function useBankAccounts(clubId: string | null) {
	return useQuery({
		queryKey: queryKeys.clubPortal.bankAccounts(clubId ?? ""),
		queryFn: () => financeApi.banking.listAccounts(clubId!),
		enabled: !!clubId,
	});
}

export function useCreateBankAccount(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateBankAccount) => financeApi.banking.createAccount(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankAccounts(clubId),
			}),
	});
}

export function useUpdateBankAccount(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ bankId, data }: { bankId: string; data: UpdateBankAccount }) =>
			financeApi.banking.updateAccount(clubId, bankId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankAccounts(clubId),
			}),
	});
}

export function useDeleteBankAccount(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (bankId: string) => financeApi.banking.deleteAccount(clubId, bankId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankAccounts(clubId),
			}),
	});
}

// --- Transactions ---

export function useBankTransactions(
	clubId: string | null,
	bankId: string,
	params?: { status?: string; from?: string; to?: string },
) {
	return useQuery({
		queryKey: queryKeys.clubPortal.bankTransactions(clubId ?? "", bankId),
		queryFn: () => financeApi.banking.listTransactions(clubId!, bankId, params),
		enabled: !!clubId && !!bankId,
	});
}

export function useImportTransactions(clubId: string, bankId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (formData: FormData) =>
			financeApi.banking.importTransactions(clubId, bankId, formData),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankTransactions(clubId, bankId),
			}),
	});
}

export function useAutoMatch(clubId: string, bankId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => financeApi.banking.autoMatch(clubId, bankId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankTransactions(clubId, bankId),
			}),
	});
}

export function useManualMatch(clubId: string, bankId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ txId, invoiceId }: { txId: string; invoiceId: string }) =>
			financeApi.banking.manualMatch(clubId, bankId, txId, invoiceId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankTransactions(clubId, bankId),
			}),
	});
}

export function useBookTransaction(clubId: string, bankId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ txId, accountId }: { txId: string; accountId: string }) =>
			financeApi.banking.bookTransaction(clubId, bankId, txId, accountId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankTransactions(clubId, bankId),
			}),
	});
}

export function useIgnoreTransaction(clubId: string, bankId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (txId: string) => financeApi.banking.ignoreTransaction(clubId, bankId, txId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bankTransactions(clubId, bankId),
			}),
	});
}
